import { navigate } from '@reach/router'
import React, { FC, useState, useEffect } from 'react'
import loadListeners from './listeners'

/**
 * The socket.io-client npm package is only installed for typing; the actual client is the
 * socket.io v4 build loaded globally via `public/index.html` (window.io).
 */

interface WebSocketInterface {
  socket: SocketIOClient.Socket | null
  loading: boolean
}

const WebSocketContext = React.createContext<WebSocketInterface>({ socket: null, loading: true })

interface OwnProps {
  locale: string
}

export const WebSocketProvider: FC<OwnProps> = (props) => {
  const [socket, setSocket] = useState<SocketIOClient.Socket | null>(null)
  const [isLoading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    const wrapper = (window.io(
      process.env.REACT_APP_WS_URI as string,
      {
        transports: ['polling', 'websocket'],
        // required so the browser sends back Azure's sticky-session cookie on every
        // polling request of the handshake (server has cors credentials enabled too);
        // cast as `any` because @types/socket.io-client is the old v1 typing (see note
        // above) and doesn't know about this v4-only option
        withCredentials: true,
      } as any
    ) as unknown) as SocketIOClient.Socket

    setSocket(wrapper)
    wrapper.on('connect', () => {
      setLoading(false)
      loadListeners(wrapper, props.locale)
    })
    wrapper.on('error', () => {
      setLoading(false)
      navigate('/error')
    })
  }, [props.locale])

  return <WebSocketContext.Provider value={{ socket: socket, loading: isLoading }} {...props} />
}

export const useWS = (): WebSocketInterface => React.useContext(WebSocketContext)
